import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, MapPin, Radar, MailOpen } from "lucide-react";
import { Logo } from "@/components/Brand";
import { OffersMarquee } from "@/components/OffersMarquee";
import { Reveal } from "@/components/Reveal";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSection, JoinBanner } from "@/components/HomeFaq";
import { MemberTrips } from "@/components/MemberTrips";
import { getHomeOffers } from "@/services/offers";

const STEPS = [
  { icon: MapPin, title: "Inscrivez-vous : nos offres partent de Paris-Charles de Gaulle (CDG)." },
  { icon: Radar, title: "Notre algorithme surveille les tarifs Business depuis Paris-CDG vers différentes destinations." },
  { icon: MailOpen, title: "Recevez les offres par e-mail et réservez vous-même." },
];

const fade = (d) => ({
  initial: { opacity: 0, y: 22 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.9, delay: d, ease: [0.22, 1, 0.36, 1] },
});

const Header = () => (
  <header className="absolute inset-x-0 top-0 z-30">
    <div className="mx-auto flex max-w-6xl items-center justify-between gap-2 px-3 py-5 sm:px-10 sm:py-7">
      <Logo />
      <div className="flex items-center gap-0.5 sm:gap-5">
        <Link to="/mon-compte" data-testid="nav-login-link" className="link-underline whitespace-nowrap px-2 py-2 text-[13px] text-ivory/85 sm:text-sm hover:text-ivory">
          Mon compte
        </Link>
        <Link to="/inscription" data-testid="nav-join-button" className="btn-gold whitespace-nowrap px-3.5 py-2 text-[13px] sm:px-4 sm:py-2.5 sm:text-sm">
          Rejoindre le club
        </Link>
      </div>
    </div>
  </header>
);

const Hero = () => (
  <section className="relative isolate flex min-h-[92svh] items-end overflow-hidden bg-navy-deep">
    <picture className="absolute inset-0 -z-10">
      <source media="(max-width: 767px)" srcSet="/images/hero-suite-mobile.jpg" />
      <motion.img
        src="/images/hero-suite.jpg"
        alt="Suite Business moderne baignée de lumière naturelle"
        data-testid="hero-image"
        className="h-full w-full object-cover object-[center_30%] md:object-center"
        initial={{ scale: 1.08 }}
        animate={{ scale: 1 }}
        transition={{ duration: 2.4, ease: [0.22, 1, 0.36, 1] }}
      />
    </picture>
    <div className="absolute inset-0 -z-10 bg-gradient-to-t from-navy-deep from-25% via-navy-deep/60 to-navy-deep/15 md:via-navy-deep/25 md:to-transparent" />
    <div className="absolute inset-0 -z-10 hidden bg-navy-deep/30 md:block" />
    <div className="absolute inset-x-0 top-0 -z-10 h-28 bg-gradient-to-b from-navy-deep/50 to-transparent" />
    <div className="mx-auto w-full max-w-6xl px-5 pb-14 pt-32 text-center sm:px-10 sm:pb-20">
      <motion.p {...fade(0.2)} className="eyebrow text-gold">Club privé · Classe Business</motion.p>
      <motion.h1 {...fade(0.35)} data-testid="hero-title" className="mx-auto mt-5 max-w-4xl font-display text-[38px] leading-[1.08] text-ivory sm:text-5xl lg:text-6xl">
        La classe Business <span className="text-gold-gradient">à un prix qui donne envie de partir</span>
      </motion.h1>
      <motion.p {...fade(0.5)} className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-ivory/75 sm:text-lg">
        Recevez des alertes lorsque les prix des vols en business au départ de Paris-CDG chutent de 20 à 60 %.
      </motion.p>
      <motion.div {...fade(0.65)} className="mx-auto mt-9 flex flex-col items-center gap-4 sm:max-w-xl">
        <Link to="/inscription" data-testid="hero-try-free-button" className="btn-gold group w-full justify-center px-7 py-4 text-base sm:w-fit">
          Essayer gratuitement
          <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </motion.div>
    </div>
  </section>
);

const Offers = () => (
  <section className="bg-navy-deep pb-16 pt-6 sm:pb-24">
    <div className="mx-auto mb-6 flex max-w-6xl flex-col gap-3 px-5 sm:mb-8 sm:flex-row sm:items-end sm:justify-between sm:px-10">
      <Reveal>
        <p className="eyebrow text-gold/80">Au départ de Paris-Charles de Gaulle (CDG)</p>
        <h2 className="mt-3 font-display text-3xl text-ivory sm:text-4xl">Le genre d'offres que vous recevrez</h2>
      </Reveal>
    </div>
    <OffersMarquee offers={getHomeOffers()} />
  </section>
);

const HowItWorks = () => (
  <section id="comment-ca-marche" className="bg-ivory px-5 py-20 sm:px-10 sm:py-28">
    <div className="mx-auto grid max-w-6xl gap-12 lg:grid-cols-[1fr_1.1fr] lg:gap-20">
      <Reveal className="relative hidden overflow-hidden rounded-[32px] lg:block">
        <img src="/images/cabin-lounge.jpg" alt="Voyageuse en cabine Business" className="h-full max-h-[620px] w-full object-cover" />
      </Reveal>
      <div className="flex flex-col justify-center">
        <Reveal>
          <p className="eyebrow text-gold-deep">Comment ça marche</p>
          <h2 className="mt-4 font-display text-4xl leading-tight text-navy sm:text-5xl">Simple, de Paris-CDG au monde.</h2>
        </Reveal>
        <ol className="mt-10 space-y-4">
          {STEPS.map((s, i) => (
            <Reveal key={s.title} delay={0.1 * i}>
              <li data-testid={`how-step-${i + 1}`} className="flex items-start gap-5 rounded-3xl border border-navy/10 bg-white/70 p-5 transition-colors duration-300 hover:border-gold/50 sm:p-6">
                <span className="font-display text-4xl leading-none text-gold-deep">{i + 1}</span>
                <div className="flex-1 pt-1">
                  <p className="text-base font-medium leading-snug text-navy sm:text-lg">{s.title}</p>
                </div>
                <s.icon className="mt-1 h-5 w-5 shrink-0 text-navy/40" strokeWidth={1.5} />
              </li>
            </Reveal>
          ))}
        </ol>
        <Reveal delay={0.35} className="mt-10">
          <Link to="/inscription" data-testid="how-it-works-join-button" className="btn-navy group w-full justify-center px-7 py-4 sm:w-fit">
            Rejoindre le club
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
          <p className="mt-4 text-xs text-navy/55">Nous ne vendons pas de billets : vous réservez directement auprès du vendeur.</p>
        </Reveal>
      </div>
    </div>
  </section>
);

export default function Home() {
  return (
    <div className="relative">
      <div className="relative">
        <Header />
        <Hero />
      </div>
      <Offers />
      <HowItWorks />
      <MemberTrips />
      <FaqSection />
      <JoinBanner />
      <SiteFooter />
    </div>
  );
}
